/**
 * VISALL 统一验证引擎
 * 🆕 集成编码适配器支持
 * 核心优化：单次遍历完成所有验证（字段检查、类型验证、业务规则）
 */

import * as utils from '../../util';
import type {
  Data,
  Spec,
  TabularData,
  LayerSpec,
  ValidationResult,
  LayerValidationResult,
  ValidationConfig,
  FieldStats,
  FieldValidationConfig
} from './types';
import { ValidationConfigLoader } from './ValidationConfigLoader';
// 🆕 导入编码兼容性管理器
import { globalEncodingManager } from './encodingCompatibility';

// 🆕 导入适配器
import { getDynamicBeeSwarmAdapters } from '../../layer/preset/scatter/dynamicBeeSwarm.adapter';
import { getDynamicBubbleAdapters } from '../../layer/preset/scatter/dynamicBubble.adapter';

/**
 * 🆕 初始化所有适配器
 * 这个函数在系统启动时调用一次
 */
export function initializeEncodingAdapters(): void {
  // 注册动态蜂群图适配器
  getDynamicBeeSwarmAdapters().forEach(adapter => {
    globalEncodingManager.registerAdapter('dynamicBeeSwarm', adapter);
  });

  // 注册动态气泡图适配器
  getDynamicBubbleAdapters().forEach(adapter => {
    globalEncodingManager.registerAdapter('dynamicBubble', adapter);
  });

  utils.logger.info('info', 'Encoding adapters initialized successfully');
}

export class ValidationEngine {
  private static adaptersInitialized = false;

  constructor() {
    // 🆕 确保适配器已注册
    ValidationEngine.ensureAdaptersInitialized();
  }

  /**
   * 🆕 确保适配器已初始化
   */
  private static ensureAdaptersInitialized(): void {
    // 使用静态标志避免重复初始化
    if (!ValidationEngine.adaptersInitialized) {
      initializeEncodingAdapters();
      ValidationEngine.adaptersInitialized = true;
    }
  }

  /**
   * 验证所有图层
   */
  validateLayers(data: Data, spec: Spec): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      layerResults: new Map(),
      errors: [],
      warnings: []
    };

    spec.layers.forEach((layer: LayerSpec) => {
      const layerResult = this.validateSingleLayer(data, layer);
      result.layerResults.set(layer.type, layerResult);
      result.isValid = result.isValid && layerResult.isValid;

      // 收集所有错误
      result.errors.push(
        ...layerResult.fieldErrors,
        ...layerResult.typeErrors,
        ...layerResult.businessErrors
      );

      // 收集所有警告
      result.warnings.push(...layerResult.warnings);
    });

    return result;
  }

  /**
   * 🆕 公开编码适配方法，供DataProcessor使用
   * @param layer - 原始图层配置
   * @returns 适配后的图层配置
   */
  public static adaptLayerEncodingForRendering(layer: LayerSpec): LayerSpec {
    return ValidationEngine.adaptLayerEncoding(layer);
  }

  /**
   *  验证单个图层（集成适配器支持）
   */
  private validateSingleLayer(data: Data, layer: LayerSpec): LayerValidationResult {
    const dataIndex = layer.dataIndex || 0;
    const values = data[dataIndex]?.values;

    // 🆕 1. 编码适配 - 在验证前转换编码格式
    const adaptedLayer = ValidationEngine.adaptLayerEncoding(layer);

    // 2. 获取验证配置（基于原始图层类型）
    const config = this.getValidationConfig(layer.type);

    // 3. 检查数据是否存在
    if (!values) {
      return {
        isValid: false,
        fieldErrors: [
          {
            type: 'no-data',
            layer: layer.type,
            message: `No data available for ${layer.type} layer`,
            suggestion: 'Please provide valid data for the layer'
          }
        ],
        typeErrors: [],
        businessErrors: [],
        warnings: []
      };
    }

    // 4. 基于适配后的编码进行验证
    if (Array.isArray(values)) {
      return this.singlePassValidation(values, adaptedLayer, config);
    }
    return this.validateNonTabularData(values, adaptedLayer, config);
  }

  /**
   * 🆕 编码适配方法
   * @param layer - 原始图层配置
   * @returns 适配后的图层配置
   */
  private static adaptLayerEncoding(layer: LayerSpec): LayerSpec {
    const originalEncoding = layer.encoding || {};
    const layerType = layer.type;

    try {
      // 获取所有可用适配器
      const availableAdapters = globalEncodingManager.getAdapters(layerType);

      if (availableAdapters.length === 0) {
        // 无适配器，直接返回
        return layer;
      }

      // 🎯 调试信息：显示适配器匹配过程
      utils.logger.debug('debug', `Checking ${availableAdapters.length} adapters for ${layerType}`);

      // 查找匹配的适配器
      const matchedAdapter = availableAdapters.find(adapter => {
        const isMatch = adapter.isMatch(originalEncoding);
        utils.logger.debug(
          'debug',
          `Adapter ${adapter.version} for ${layerType}: ${isMatch ? 'MATCH' : 'NO_MATCH'}`
        );
        return isMatch;
      });

      if (matchedAdapter) {
        // 执行转换
        const adaptedEncoding = matchedAdapter.transform(originalEncoding);

        // 🎯 详细的适配日志
        utils.logger.info(
          'info',
          `🔄 Encoding adapted for ${layerType} (${matchedAdapter.version}):\n` +
            `  Original: ${JSON.stringify(originalEncoding, null, 2)}\n` +
            `  Adapted:  ${JSON.stringify(adaptedEncoding, null, 2)}`
        );

        const adaptedLayer = {
          ...layer,
          encoding: adaptedEncoding as Record<string, string | string[]>,
          // 🆕 添加元数据记录适配信息
          __adaptationInfo: {
            originalEncoding,
            adaptedEncoding,
            adapterVersion: matchedAdapter.version,
            adaptedAt: new Date().toISOString()
          }
        } as LayerSpec;

        // 验证适配结果
        if (ValidationEngine.validateAdaptedEncoding(adaptedLayer, layer)) {
          return adaptedLayer;
        }

        // 验证失败时回退到原始编码
        utils.logger.warn(
          'warning',
          `Adapted encoding validation failed for ${layerType}, using original encoding`
        );
        return layer;
      }

      // 无匹配适配器
      utils.logger.debug('debug', `No adapter matched for ${layerType}, using original encoding`);

      return layer;
    } catch (error: unknown) {
      // 🚨 详细的错误处理
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;

      utils.logger.error('error', `Encoding adaptation failed for ${layerType}:`, {
        error: errorMessage,
        stack: errorStack,
        originalEncoding,
        layerType
      });

      // 适配失败时使用原始编码
      return layer;
    }
  }

  /**
   * 🆕 适配结果验证
   * 确保适配后的编码符合预期格式
   */
  private static validateAdaptedEncoding(
    adaptedLayer: LayerSpec,
    originalLayer: LayerSpec
  ): boolean {
    try {
      const adaptedEncoding = adaptedLayer.encoding || {};
      const originalEncoding = originalLayer.encoding || {};

      // 基本检查：适配后的编码不应为空
      if (Object.keys(adaptedEncoding).length === 0 && Object.keys(originalEncoding).length > 0) {
        utils.logger.warn(
          'warning',
          `Adaptation resulted in empty encoding for ${originalLayer.type}`
        );
        return false;
      }

      // 🎯 可以在这里添加更多验证逻辑
      // 例如：检查必需字段是否存在、类型是否正确等

      return true;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      utils.logger.error('error', `Adapted encoding validation failed: ${errorMessage}`);
      return false;
    }
  }

  /**
   * 🆕 验证非表格数据（如树图、网络图等）
   */
  private validateNonTabularData(
    values: Record<string, unknown> | object,
    layer: LayerSpec,
    config: ValidationConfig
  ): LayerValidationResult {
    const result: LayerValidationResult = {
      isValid: true,
      fieldErrors: [],
      typeErrors: [],
      businessErrors: [],
      warnings: []
    };

    // 对于非表格数据，只执行业务规则验证
    this.validateBusinessRules(values, layer, result, config);

    return result;
  }

  /**
   * 🎯 核心优化：单次遍历验证
   * 解决数据多次遍历的性能问题
   */
  private singlePassValidation(
    values: TabularData,
    layer: LayerSpec,
    config: ValidationConfig
  ): LayerValidationResult {
    const result: LayerValidationResult = {
      isValid: true,
      fieldErrors: [],
      typeErrors: [],
      businessErrors: [],
      warnings: []
    };

    // 检查数据是否为空
    if (!values.length) {
      result.isValid = false;
      result.fieldErrors.push({
        type: 'no-data',
        layer: layer.type,
        message: `No data available for ${layer.type} layer`,
        suggestion: 'Please provide valid data array with at least one record'
      });
      return result;
    }

    // 🎯 1. 修正的字段存在性检查（收集所有字段，解决只检查第一行的问题）
    const existingFields = new Set<string>();
    values.forEach(row => {
      Object.keys(row).forEach(field => existingFields.add(field));
    });
    ValidationEngine.checkFieldExistence(existingFields, layer, result, config);

    // 🎯 2. 单次遍历：同时完成类型验证、统计收集
    const fieldStats = new Map<string, FieldStats>();

    values.forEach((row, index) => {
      // 数据类型验证
      this.validateRowDataTypes(row, layer, result, config);

      // 收集字段统计信息
      this.collectFieldStats(row, layer, fieldStats, index === 0);
    });

    // 🎯 3. 业务规则验证（使用完整原始数据）
    this.validateBusinessRules(values, layer, result, config);

    return result;
  }

  /**
   * 字段存在性检查（修正：检查所有数据行的字段）
   */
  private static checkFieldExistence(
    existingFields: Set<string>,
    layer: LayerSpec,
    result: LayerValidationResult,
    config: ValidationConfig
  ): void {
    const { encoding = {} } = layer;

    // 检查每个编码映射的字段是否存在
    Object.entries(encoding).forEach(([encodingKey, fieldNames]) => {
      const fields = Array.isArray(fieldNames) ? fieldNames : [fieldNames];

      fields.forEach(fieldName => {
        if (!existingFields.has(fieldName)) {
          result.isValid = false;
          result.fieldErrors.push({
            type: 'field-not-found',
            encoding: encodingKey,
            field: fieldName,
            layer: layer.type,
            message: `Field "${fieldName}" mapped to "${encodingKey}" does not exist in data`,
            suggestion: `Check if field name is correct or add "${fieldName}" to your data`
          });

          // 保持与现有逻辑兼容的警告
          utils.logger.warn(
            'warning',
            `In encoding where case is ${layer.type}: map "${fieldName}" does not exist in the raw data`
          );
        }
      });
    });
  }

  /**
   * 数据类型验证
   */
  private validateRowDataTypes(
    row: Record<string, unknown>,
    layer: LayerSpec,
    result: LayerValidationResult,
    config: ValidationConfig
  ): void {
    const { encoding = {} } = layer;

    config.fields.forEach(fieldConfig => {
      this.validateFieldConfig(row, layer, result, fieldConfig, encoding);
    });
  }

  /**
   * 验证单个字段配置
   */
  private validateFieldConfig(
    row: Record<string, unknown>,
    layer: LayerSpec,
    result: LayerValidationResult,
    fieldConfig: FieldValidationConfig,
    encoding: Record<string, string | string[]>
  ): void {
    const { field, type, required = true } = fieldConfig;

    // 跳过非必需且不存在的字段
    if (!required && !encoding[field]) {
      return;
    }

    const fieldValue = encoding[field];
    if (!fieldValue) {
      return;
    }

    const fieldValues = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
    fieldValues.forEach(singleField => {
      this.validateSingleFieldValue(row, layer, result, singleField, field, type, required);
    });
  }

  /**
   * 验证单个字段值
   */
  private validateSingleFieldValue(
    row: Record<string, unknown>,
    layer: LayerSpec,
    result: LayerValidationResult,
    singleField: string,
    field: string,
    type: FieldValidationConfig['type'],
    required: boolean
  ): void {
    const value = row[singleField];

    // 🆕 稀疏数据支持：非必需字段允许undefined/null/空值
    if (!required && (value === undefined || value === null || value === '')) {
      // 跳过验证，允许稀疏数据
      return;
    }

    if (!this.isValidType(value, type)) {
      // eslint-disable-next-line no-param-reassign
      result.isValid = false;
      result.typeErrors.push({
        type: 'type-mismatch',
        layer: layer.type,
        field: singleField,
        encoding: field,
        message: `Field "${singleField}" must be of type ${
          Array.isArray(type) ? type.join(' or ') : type
        }`,
        suggestion: `Convert field "${singleField}" to ${Array.isArray(type) ? type[0] : type} type`
      });

      // 保持与现有逻辑兼容的警告
      utils.logger.warn(
        'warning',
        `The "${field}" field of the "${layer.type}" layer must be ${
          Array.isArray(type) ? `one of: ${type.join(', ')}` : `a ${type}`
        }`
      );
    }
  }

  /**
   * 类型检查逻辑
   */
  private isValidType(value: unknown, expectedType: FieldValidationConfig['type']): boolean {
    // 处理多类型情况
    if (Array.isArray(expectedType)) {
      return expectedType.some(type => ValidationEngine.checkSingleType(value, type));
    }

    return ValidationEngine.checkSingleType(value, expectedType);
  }

  /**
   * 单一类型检查
   */
  private static checkSingleType(value: unknown, type: string): boolean {
    switch (type) {
      case 'string':
        return typeof value === 'string';
      case 'number':
        return (
          typeof Number(value) === 'number' ||
          (!Number.isNaN(Number(value)) && typeof value !== 'boolean')
        );
      case 'date':
        return (
          typeof value === 'string' && value.length > 0
        );
      case 'array':
        return Array.isArray(value);
      default:
        return false;
    }
  }

  /**
   * 收集字段统计信息
   */
  private collectFieldStats(
    row: Record<string, unknown>,
    layer: LayerSpec,
    fieldStats: Map<string, FieldStats>,
    isFirstRow: boolean
  ): void {
    Object.entries(row).forEach(([fieldName, value]) => {
      if (!fieldStats.has(fieldName)) {
        fieldStats.set(fieldName, {
          fieldName,
          totalCount: 0,
          nullCount: 0,
          uniqueValues: new Set(),
          dataTypes: new Set(),
          samples: []
        });
      }

      const stats = fieldStats.get(fieldName)!;
      stats.totalCount++;

      if (value === null || value === undefined) {
        stats.nullCount++;
      } else {
        stats.uniqueValues.add(value);
        stats.dataTypes.add(typeof value);

        // 收集前10个样本
        if (stats.samples.length < 10) {
          stats.samples.push(value);
        }
      }
    });
  }

  /**
   * 业务规则验证（更新：支持非表格数据）
   */
  private validateBusinessRules(
    values: TabularData | Record<string, unknown> | object,
    layer: LayerSpec,
    result: LayerValidationResult,
    config: ValidationConfig
  ): void {
    const rules = config.businessRules || [];
    if (rules.length === 0) {
      return;
    }

    // 执行配置中的业务规则
    rules.forEach(rule => {
      try {
        // ✅ 直接传递原始数据，支持不同数据结构
        if (!rule.validator(values, layer)) {
          // 获取规则严重性，默认为'warning'
          const severity = rule.severity || 'warning';

          const validationError = {
            type: 'business-rule' as const,
            layer: layer.type,
            message: rule.message,
            suggestion: rule.suggestion,
            severity
          };

          // 根据严重性级别决定处理方式
          if (severity === 'error') {
            // 错误级别：影响图表渲染
            // eslint-disable-next-line no-param-reassign
            result.isValid = false;
            result.businessErrors.push(validationError);
          } else {
            // 警告级别：不影响图表渲染，仅作为建议
            result.warnings.push(validationError);
          }
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error(`Business rule "${rule.name}" failed for layer ${layer.type}:`, errorMessage);
      }
    });
  }

  /**
   * 获取验证配置（使用动态配置加载器）
   */
  private getValidationConfig(layerType: string): ValidationConfig {
    return ValidationConfigLoader.getConfig(layerType);
  }
}
